import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { setPassword } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'

interface SetPasswordFormValues {
  token: string
  password: string
}

/**
 * `POST /api/auth/set-password` (task 6/040 design §4.1). The missing half of
 * the provisioning chain: a provisioned user gets a setup code by email and,
 * before this screen, had nowhere to type it.
 *
 * `token` pre-fills from `?token=` when the link carries one (the normal
 * case — a user clicks the emailed link) but stays editable, since the brief
 * treats it as an ordinary form field ("Setup code"), not a hidden one — a
 * user who mistypes or is asked to key in a code read aloud still has
 * somewhere to fix it.
 *
 * Same rendering rule as `LoginRoute`: the server's `error` string is shown
 * VERBATIM, never generalised. Task 6 specifically distinguishes "this setup
 * link is invalid or has already been used" (a dead/consumed token) from a
 * genuine fault, and separately made an over-length password stop being
 * misreported as the former — collapsing either back to one generic message
 * here would undo both distinctions at the only place a user could read them.
 *
 * On success the route SIGNS THE USER IN (routes.ts's own doc comment: doing
 * so is deliberate — the token just redeemed is stronger evidence of
 * identity than the password they just chose), so this screen invalidates
 * `['session', 'me']` and navigates home exactly like `LoginRoute` — see that
 * file's doc comment and `session.tsx`'s for why the invalidation matters and
 * how it was verified.
 */
export function SetPasswordRoute() {
  const [searchParams] = useSearchParams()
  const tokenFromLink = searchParams.get('token') ?? ''
  const form = useForm<SetPasswordFormValues>({ defaultValues: { token: tokenFromLink, password: '' } })
  const [serverError, setServerError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const onSubmit = async (values: SetPasswordFormValues) => {
    setServerError(null)
    setSubmitting(true)
    try {
      // `setPassword` (`api/client.ts`) now handles every failure shape
      // itself (a transport rejection, or a response whose body isn't usable
      // JSON) and always resolves rather than throwing — the server's own
      // `error` string still reaches the alert verbatim when present, and a
      // non-JSON body now falls back to a human sentence instead of
      // rejecting into the `catch` below (web-pre-deploy item 3).
      const result = await setPassword(values)
      if (!result.ok) {
        setServerError(result.message)
        return
      }
      // `refetchType: 'all'` for the same reason as `LoginRoute`'s identical
      // line — see that file's comment for the full mechanism and the real
      // device that reproduced it. This route signs the user in exactly as
      // `signIn` does, so it inherits the same defect wherever a
      // pre-sign-in `null` is already cached under `['session','me']`.
      //
      // NOT INDEPENDENTLY COVERED BY A TEST, stated rather than implied:
      // reaching the broken state needs `AppShell` to have mounted and
      // cached that `null` EARLIER IN THE SAME PAGE SESSION, and there is no
      // in-app link from anywhere to `/set-password` — a setup link is a
      // full page load, which starts with an empty cache and cannot hit
      // this. So the defect is latent here today, not live: fixed because it
      // is the same one line and the same one-word fix, not because a test
      // caught it. Adding any link into this route from a signed-out screen
      // would arm it, and would also make it testable the same way
      // `test/auth.test.tsx` tests the login path.
      await queryClient.invalidateQueries({ queryKey: ['session', 'me'], refetchType: 'all' })
      navigate('/')
    } catch {
      // `setPassword` no longer throws for a transport rejection or an
      // unparsable error body — see its own doc comment (`api/client.ts`) —
      // so this branch is reached only for something outside its scope
      // entirely: `invalidateQueries` or `navigate` above.
      //
      // The thrown value is DISCARDED, never rendered — final-review bucket 1
      // item 3. See `LoginRoute.tsx`'s twin of this block for the full
      // reasoning, including why no test covers this branch. This screen is
      // the one a newly-provisioned user MUST get through before they have a
      // password at all, so it is the last place a library's internal
      // exception string belongs.
      setServerError('Could not set your password.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-sm flex-col justify-center gap-6 p-6">
      <h1 className="text-xl font-semibold">Set your password</h1>
      <Form {...form}>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            control={form.control}
            name="token"
            rules={{ required: 'Setup code is required.' }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Setup code</FormLabel>
                <FormControl>
                  <Input autoComplete="one-time-code" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            rules={{ required: 'New password is required.' }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>New password</FormLabel>
                <FormControl>
                  <Input type="password" autoComplete="new-password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {serverError && (
            <p role="alert" className="text-sm text-destructive">
              {serverError}
            </p>
          )}
          <Button type="submit" disabled={submitting}>
            Set password
          </Button>
        </form>
      </Form>
    </main>
  )
}
